// src/components/MenuScreen.ts — Menú principal Fase 5 con Perfil/Equipo/Inventario/Misiones/Registro
import { store } from '../app/state.js';
import { saveLocal } from '../app/storage.js';
import { getSpecies } from '../data/asterias.js';
import { getItem, CATEGORY_LABELS, ITEM_LIST } from '../data/items.js';
import { MISSIONS } from '../data/missions.js';
import { getMissionDef } from '../data/missions.js';
import * as MissionSystem from '../systems/missionSystem.js';
import { GAME_CONFIG } from '../data/gameConfig.js';
import type { AsteriaInstance } from '../entities/asteria.js';

export function createMenuScreen(initialTab: 'perfil'|'equipo'|'inventario'|'misiones'|'registro'= 'perfil', onClose:()=>void): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText='position:absolute;inset:0;background:rgba(7,11,26,.88);backdrop-filter:blur(10px);display:grid;place-items:center;padding:1rem;z-index:18;overflow:auto;';
  let currentTab = initialTab;

  const render = () => {
    const s = store.get();
    const trainer = s.trainer;
    const aureos = s.currency;
    const totalSeen = Object.values(s.registry).filter(r=>r.seen).length;
    const totalCaught = Object.values(s.registry).filter(r=>r.caught).length;

    el.innerHTML = `
      <div style="width:min(920px,98vw);max-height:92vh;overflow:auto;background:linear-gradient(180deg, rgba(18,26,51,.98), rgba(14,20,42,.99));border:1px solid rgba(255,255,255,.12);border-radius:18px;box-shadow:0 20px 60px rgba(0,0,0,.6);display:grid;grid-template-rows:auto 1fr;">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:.75rem;padding:1rem;border-bottom:1px solid rgba(255,255,255,.08);position:sticky;top:0;background:inherit;z-index:2">
          <div style="display:flex;gap:.5rem;flex-wrap:wrap">
            ${(['perfil','equipo','inventario','misiones','registro'] as const).map(tab=>`
              <button data-tab="${tab}" type="button" class="btn ${currentTab===tab?'btn--primary':'btn--subtle'}" style="width:auto;padding:.5rem .75rem;font-size:.78rem">${labelFor(tab)}</button>
            `).join('')}
          </div>
          <div style="display:flex;gap:.5rem;align-items:center">
            <span class="chip" style="background:rgba(255,207,107,.15)">💰 ${aureos} Aurel</span>
            <span class="chip">Nv.${trainer.level} · ${trainer.xp}/${trainer.xpToNext} XP</span>
            <button data-close type="button" class="btn btn--ghost" style="width:auto;padding:.5rem .75rem">✕</button>
          </div>
        </div>
        <div style="padding:1rem" data-role="content"></div>
      </div>
    `;

    el.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach(btn=>{
      btn.addEventListener('click',()=> { currentTab = btn.dataset.tab as any; render(); });
    });
    el.querySelector<HTMLButtonElement>('[data-close]')!.addEventListener('click', onClose);

    const content = el.querySelector('[data-role="content"]') as HTMLElement;

    if(currentTab==='perfil'){
      content.innerHTML = `
        <div style="display:grid;gap:.8rem">
          <div style="display:flex;gap:1rem;align-items:center;flex-wrap:wrap">
            <div style="width:64px;height:64px;border-radius:50%;background:radial-gradient(circle at 30% 30%, #fff, #7cf0ff 40%, #6a5cff 70%);border:2px solid rgba(255,255,255,.3)"></div>
            <div>
              <div style="font-family:var(--font-display);font-weight:800;font-size:1.3rem">${s.profile.name} <span style="font-family:var(--font-mono);font-size:.8rem;color:var(--muted)">${s.profile.title}</span></div>
              <div style="font-family:var(--font-mono);font-size:.78rem;color:var(--muted)">Entrenador Nv.${trainer.level} · ${trainer.xp}/${trainer.xpToNext} XP · ${s.stats.playtimeSeconds/60|0} min jugados</div>
              <div style="height:6px;border-radius:999px;background:rgba(255,255,255,.08);overflow:hidden;margin-top:.4rem;width:220px"><div style="height:100%;width:${(trainer.xp/trainer.xpToNext*100)}%;background:linear-gradient(90deg, #6a5cff, #7cf0ff)"></div></div>
            </div>
            <div style="margin-left:auto;display:grid;gap:.4rem">
              <div class="chip">📦 ${s.team.asterias.length}/${GAME_CONFIG.maxTeamSize} equipo · ${s.team.boxed.length} caja</div>
              <div class="chip">👁️ ${totalSeen}/6 vistas · ${totalCaught} capturadas</div>
              <div class="chip">🏆 ${s.stats.battlesWon} victorias · ${s.stats.missionsCompleted??0} misiones</div>
            </div>
          </div>

          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:.6rem">
            <div style="background:rgba(255,255,255,.04);border-radius:12px;padding:.6rem;text-align:center"><div style="font-family:var(--font-mono);font-size:.66rem;color:var(--muted)">PASOS</div><div style="font-weight:800">${s.stats.stepsWalked}</div></div>
            <div style="background:rgba(255,255,255,.04);border-radius:12px;padding:.6rem;text-align:center"><div style="font-family:var(--font-mono);font-size:.66rem;color:var(--muted)">ENCUENTROS</div><div style="font-weight:800">${s.stats.encounters}</div></div>
            <div style="background:rgba(255,255,255,.04);border-radius:12px;padding:.6rem;text-align:center"><div style="font-family:var(--font-mono);font-size:.66rem;color:var(--muted)">OBJETOS</div><div style="font-weight:800">${s.stats.objectsCollected??0}</div></div>
          </div>

          <div style="background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.06);border-radius:12px;padding:.75rem">
            <div style="font-weight:800;margin-bottom:.4rem">Progreso general</div>
            <div style="font-size:.82rem;color:var(--muted);line-height:1.6">
              Villa Aurelia y Ruta Aurelia exploradas. ${s.world.discoveredZones.length} zonas descubiertas.<br/>
              Siguiente nivel entrenador: ${trainer.xpToNext - trainer.xp} XP restantes.<br/>
              Polvo de vínculo disponible para futuras evoluciones.
            </div>
          </div>

          <div style="display:flex;gap:.5rem;flex-wrap:wrap">
            <span class="chip">Medallas: próximamente</span><span class="chip">Logros: ${Object.keys(s.world.flags).length} flags</span>
          </div>
        </div>
      `;
    } else if(currentTab==='equipo'){
      const team = s.team.asterias;
      const boxed = s.team.boxed;
      content.innerHTML = `
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:.8rem">
          <div style="border:1px solid rgba(255,255,255,.08);border-radius:14px;overflow:hidden;background:rgba(255,255,255,.02)">
            <div style="padding:.6rem .75rem;font-weight:800;display:flex;justify-content:space-between"><span>Equipo activo</span><span class="chip">${team.length}/${GAME_CONFIG.maxTeamSize}</span></div>
            <div data-role="team-list" style="display:grid;gap:.5rem;padding:.5rem"></div>
            ${team.length===0?`<div style="padding:1rem;text-align:center;color:var(--muted);font-size:.86rem">Sin Asterias aún. Ve a la hierba alta de Ruta Aurelia.</div>`:''}
            <div style="padding:.5rem"><button data-sort-team type="button" class="btn btn--subtle" style="width:auto;padding:.4rem .6rem;font-size:.72rem">↕ Ordenar: mover líder arriba</button></div>
          </div>
          <div style="border:1px solid rgba(255,255,255,.08);border-radius:14px;overflow:hidden;background:rgba(255,255,255,.02)">
            <div style="padding:.6rem .75rem;font-weight:800">Almacenamiento</div>
            <div data-role="box-list" style="display:grid;gap:.5rem;padding:.5rem;max-height:360px;overflow:auto"></div>
            ${boxed.length===0?`<div style="padding:.6rem .75rem;color:var(--muted);font-size:.82rem">Vacío</div>`:''}
          </div>
        </div>
        <div data-role="detail" style="margin-top:.8rem"></div>
      `;
      const teamList = content.querySelector('[data-role="team-list"]') as HTMLElement;
      const boxList = content.querySelector('[data-role="box-list"]') as HTMLElement;
      const detailEl = content.querySelector('[data-role="detail"]') as HTMLElement;

      const card = (inst: AsteriaInstance, idx:number, inBox:boolean)=>{
        const sp = getSpecies(inst.speciesId)!;
        const d=document.createElement('div');
        d.style.cssText='display:flex;gap:.6rem;align-items:center;padding:.5rem;border-radius:10px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.06);cursor:pointer';
        d.innerHTML=`
          <div style="width:44px;height:44px;border-radius:10px;background:${sp.color};display:grid;place-items:center;border:1px solid rgba(255,255,255,.14);font-size:18px">✦</div>
          <div style="flex:1;min-width:0">
            <div style="font-weight:800;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${sp.name} <span style="font-family:var(--font-mono);font-size:.72rem;color:var(--muted)">Nv.${inst.level} ${inst.nickname?`“${inst.nickname}”`:''}</span></div>
            <div style="font-family:var(--font-mono);font-size:.68rem;color:var(--muted)">${sp.element} · PS ${inst.hp}/${inst.maxHp} · EN ${inst.energy}/${inst.maxEnergy} · Vínculo ${inst.bond}</div>
            <div style="height:4px;border-radius:999px;background:rgba(255,255,255,.08);margin-top:.2rem;overflow:hidden"><div style="height:100%;width:${inst.hp/inst.maxHp*100}%;background:linear-gradient(90deg, #3dd68c, #7cf0ff)"></div></div>
            <div style="height:3px;border-radius:999px;background:rgba(255,207,107,.15);margin-top:.2rem;overflow:hidden"><div style="height:100%;width:${inst.xp/inst.xpToNext*100}%;background:linear-gradient(90deg, #6a5cff, #7cf0ff)"></div></div>
          </div>
          <span class="chip" style="font-size:.66rem">${inBox?'Caja':'Equipo'}</span>
        `;
        d.addEventListener('click',()=> showDetail(inst, idx, inBox));
        return d;
      };
      team.forEach((inst,i)=> teamList.appendChild(card(inst,i,false)));
      boxed.forEach((inst,i)=> boxList.appendChild(card(inst,i,true)));

      content.querySelector<HTMLButtonElement>('[data-sort-team]')?.addEventListener('click',()=>{
        if(s.team.asterias.length<2) return;
        const first = s.team.asterias.shift()!;
        s.team.asterias.push(first);
        store.setState(s); saveLocal(s); render();
      });

      const showDetail = (inst:AsteriaInstance, idx:number, inBox:boolean)=>{
        const sp = getSpecies(inst.speciesId)!;
        detailEl.innerHTML=`
          <div style="border:1px solid rgba(124,240,255,.2);border-radius:14px;padding:.75rem;background:rgba(124,240,255,.06)">
            <div style="display:flex;justify-content:space-between;gap:.5rem;align-items:center"><span style="font-family:var(--font-display);font-weight:800">${sp.name} · Nv.${inst.level}</span><span class="chip">${sp.role} · ${sp.element}</span></div>
            <div style="font-family:var(--font-mono);font-size:.74rem;color:var(--muted)">${sp.abilityId} · Capturada en ${inst.caughtZone} · Vínculo ${inst.bond}/100 · ${inst.status? `Estado ${inst.status.id} (${inst.status.turns})` : 'Sin estado'}</div>
            <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:.5rem;margin-top:.6rem">
              <div style="background:rgba(0,0,0,.2);border-radius:10px;padding:.5rem;text-align:center"><div style="font-family:var(--font-mono);font-size:.66rem;color:var(--muted)">PS</div><div style="font-weight:800">${inst.hp}/${inst.maxHp}</div></div>
              <div style="background:rgba(0,0,0,.2);border-radius:10px;padding:.5rem;text-align:center"><div style="font-family:var(--font-mono);font-size:.66rem;color:var(--muted)">ATK/DEF</div><div style="font-weight:800">${inst.atk}/${inst.def}</div></div>
              <div style="background:rgba(0,0,0,.2);border-radius:10px;padding:.5rem;text-align:center"><div style="font-family:var(--font-mono);font-size:.66rem;color:var(--muted)">VEL/EN</div><div style="font-weight:800">${inst.spd}/${inst.energy}</div></div>
              <div style="background:rgba(0,0,0,.2);border-radius:10px;padding:.5rem;text-align:center"><div style="font-family:var(--font-mono);font-size:.66rem;color:var(--muted)">XP</div><div style="font-weight:800">${inst.xp}/${inst.xpToNext}</div></div>
            </div>
            <div style="margin-top:.5rem;display:flex;gap:.4rem;flex-wrap:wrap">${inst.moves.map(mid=>{ const m=getItem(mid)?? {name:mid} as any; const mv = (sp.moves.find(x=>x.id===mid)); return `<span class="chip">${mv? mv.name : mid}</span>`}).join('')}</div>
            <div style="margin-top:.6rem;display:flex;gap:.5rem;flex-wrap:wrap">
              ${inBox?`<button data-to-team type="button" class="btn btn--ghost" style="width:auto;padding:.5rem .75rem">↗ Al equipo</button>`:`<button data-to-box type="button" class="btn btn--subtle" style="width:auto;padding:.5rem .75rem">↘ A caja</button>`}
              <button data-leader type="button" class="btn btn--subtle" style="width:auto;padding:.5rem .75rem">👑 Hacer líder</button>
              <button data-release type="button" class="btn btn--subtle" style="width:auto;padding:.5rem .75rem;color:var(--danger)">Liberar</button>
            </div>
          </div>
        `;
        detailEl.querySelector<HTMLButtonElement>('[data-to-team]')?.addEventListener('click',()=>{
          if(s.team.asterias.length>=GAME_CONFIG.maxTeamSize){ notify('Equipo lleno (6)', 'warning'); return; }
          const [m]=s.team.boxed.splice(idx,1); s.team.asterias.push(m); store.setState(s); saveLocal(s); render();
        });
        detailEl.querySelector<HTMLButtonElement>('[data-to-box]')?.addEventListener('click',()=>{
          const [m]=s.team.asterias.splice(idx,1); s.team.boxed.push(m); store.setState(s); saveLocal(s); render();
        });
        detailEl.querySelector<HTMLButtonElement>('[data-leader]')?.addEventListener('click',()=>{
          const arr = inBox? s.team.boxed : s.team.asterias;
          const [m]=arr.splice(idx,1); arr.unshift(m); store.setState(s); saveLocal(s); render();
        });
        detailEl.querySelector<HTMLButtonElement>('[data-release]')?.addEventListener('click',()=>{
          if(!confirm(`¿Liberar a ${sp.name} Nv.${inst.level}?`)) return;
          if(inBox) s.team.boxed.splice(idx,1); else s.team.asterias.splice(idx,1);
          store.setState(s); saveLocal(s); render(); detailEl.innerHTML='';
        });
      };
    } else if(currentTab==='inventario'){
      const cats: (import('../data/items.js').ItemCategory| 'todos')[] = ['todos','curacion','captura','combate','mision','clave','especial'];
      let filter: typeof cats[number] = 'todos';
      const renderInv = () => {
        const s2 = store.get();
        const items = s2.inventory;
        content.innerHTML = `
          <div style="display:flex;gap:.4rem;flex-wrap:wrap;margin-bottom:.6rem">
            ${cats.map(c=> `<button data-cat="${c}" type="button" class="btn ${filter===c?'btn--primary':'btn--subtle'}" style="width:auto;padding:.4rem .6rem;font-size:.72rem">${c==='todos'?'Todos': CATEGORY_LABELS[c as import('../data/items.js').ItemCategory] ?? c}</button>`).join('')}
          </div>
          <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:.5rem">
            ${items.filter(it=>{
              if(filter==='todos') return true;
              const def=getItem(it.id); return def?.category===filter;
            }).map(it=>{
              const def=getItem(it.id);
              return `<div style="border:1px solid rgba(255,255,255,.08);border-radius:12px;padding:.6rem;background:rgba(255,255,255,.03)">
                <div style="display:flex;justify-content:space-between;align-items:center"><span style="font-weight:800">${def?.icon ?? ''} ${def?.name ?? it.id}</span><span class="chip">×${it.qty}</span></div>
                <div style="font-size:.74rem;color:var(--muted);margin-top:.2rem">${def?.description ?? ''}</div>
                <div style="display:flex;gap:.3rem;margin-top:.4rem;flex-wrap:wrap"><span class="chip" style="font-size:.62rem">${def?.category ?? ''}</span><span class="chip" style="font-size:.62rem">${def?.price? `${def.price} Aurel`:''}</span></div>
                <div style="margin-top:.5rem;display:flex;gap:.4rem">
                  <button data-use="${it.id}" type="button" class="btn btn--ghost" style="padding:.35rem .5rem;font-size:.72rem;flex:1" ${def?.usableOutBattle? '':'disabled'}>Usar</button>
                  <button data-sell="${it.id}" type="button" class="btn btn--subtle" style="padding:.35rem .5rem;font-size:.72rem" ${def?.keyItem? 'disabled title="No vendible"':''}>Vender</button>
                </div>
              </div>`;
            }).join('') || '<div style="color:var(--muted);grid-column:1/-1;text-align:center;padding:1rem">Inventario vacío en esta categoría</div>'}
          </div>
          <div style="margin-top:.6rem;font-size:.78rem;color:var(--muted)">Dinero: <strong>${s2.currency} Aurel</strong> · Total objetos: ${items.reduce((a,b)=>a+b.qty,0)}</div>
        `;
        content.querySelectorAll<HTMLButtonElement>('[data-cat]').forEach(btn=> btn.addEventListener('click',()=>{ filter=btn.dataset.cat as any; renderInv(); }));
        content.querySelectorAll<HTMLButtonElement>('[data-use]').forEach(btn=>{
          btn.addEventListener('click',()=>{
            const id=btn.dataset.use!;
            const def=getItem(id);
            if(!def?.usableOutBattle) return;
            const s3=store.get();
            const it=s3.inventory.find(i=>i.id===id);
            if(!it || it.qty<=0) return;
            // curación simple fuera de combate
            if(id==='pocion_aurea' || id==='pan_aureliano'){
              const target = s3.team.asterias.find(a=> a.hp < a.maxHp) ?? s3.team.asterias[0];
              if(!target){ notify('No hay Asteria para curar', 'warning'); return; }
              const heal = id==='pocion_aurea'? 35 : 15;
              target.hp = Math.min(target.maxHp, target.hp + heal);
              it.qty--; if(it.qty<=0) s3.inventory = s3.inventory.filter(x=> x.id!==id);
              store.setState(s3); saveLocal(s3);
              notify(`+${heal} PS a ${getSpecies(target.speciesId)?.name}`, 'success');
              renderInv();
            } else if(id==='cristal_lumen'){
              const t = s3.team.asterias.find(a=> a.energy < a.maxEnergy);
              if(!t){ notify('Energía al máximo', 'info'); return; }
              t.energy = Math.min(t.maxEnergy, t.energy+3);
              t.status=null;
              it.qty--; if(it.qty<=0) s3.inventory = s3.inventory.filter(x=> x.id!==id);
              store.setState(s3); saveLocal(s3); notify('Cristal usado', 'success'); renderInv();
            } else if(id==='baya_lumen' || id==='antidoto_aureo'){
              const t = s3.team.asterias.find(a=> a.status);
              if(!t){ notify('Nadie con estado alterado', 'info'); return; }
              t.status=null;
              it.qty--; if(it.qty<=0) s3.inventory = s3.inventory.filter(x=> x.id!==id);
              store.setState(s3); saveLocal(s3); notify('Estado curado', 'success'); renderInv();
            }
          });
        });
        content.querySelectorAll<HTMLButtonElement>('[data-sell]').forEach(btn=>{
          btn.addEventListener('click',()=>{
            const id=btn.dataset.sell!;
            if(!confirm(`¿Vender 1× ${getItem(id)?.name} por ${getItem(id)?.sellPrice} Aurel?`)) return;
            const s3=store.get();
            const def=getItem(id);
            if(!def || def.keyItem) return;
            const it=s3.inventory.find(i=>i.id===id);
            if(!it || it.qty<=0) return;
            it.qty--; if(it.qty<=0) s3.inventory = s3.inventory.filter(x=> x.id!==id);
            s3.currency += def.sellPrice;
            s3.trainer.aureos = s3.currency;
            store.setState(s3); saveLocal(s3); notify(`+${def.sellPrice} Aurel`, 'success'); renderInv();
          });
        });
      };
      renderInv();
    } else if(currentTab==='misiones'){
      const s2 = store.get();
      const allMissions = s2.missions;
      content.innerHTML = `
        <div style="display:grid;gap:.6rem">
          ${allMissions.map(ms=>{
            const def = getMissionDef(ms.id);
            if(!def) return '';
            const statusColor = ms.status==='activa'?'#7cf0ff': ms.status==='completada'?'#3dd68c': ms.status==='entregada'?'#9aa3c7': ms.status==='disponible'?'#ffcf6b':'#6b759e';
            return `<div style="border:1px solid rgba(255,255,255,.08);border-radius:12px;padding:.7rem;background:${ms.status==='activa'?'rgba(124,240,255,.06)': ms.status==='completada'?'rgba(61,214,140,.06)':'rgba(255,255,255,.02)'}">
              <div style="display:flex;justify-content:space-between;gap:.5rem;align-items:center;flex-wrap:wrap">
                <div>
                  <div style="font-weight:800">${def.name} <span style="font-family:var(--font-mono);font-size:.68rem;color:${statusColor}">· ${ms.status.toUpperCase()}</span></div>
                  <div style="font-size:.78rem;color:var(--muted)">${def.description}</div>
                </div>
                <span class="chip">${def.type}</span>
              </div>
              <div style="margin-top:.5rem;display:grid;gap:.3rem">
                ${ms.objectives.map(o=>`
                  <div style="display:flex;justify-content:space-between;align-items:center;background:rgba(0,0,0,.2);border-radius:8px;padding:.4rem .5rem">
                    <span style="font-size:.78rem">${o.description}</span>
                    <span style="font-family:var(--font-mono);font-size:.72rem" class="chip">${o.current}/${o.required}</span>
                  </div>
                `).join('')}
              </div>
              <div style="margin-top:.5rem;height:6px;border-radius:999px;background:rgba(255,255,255,.08);overflow:hidden"><div style="height:100%;width:${ms.progress*100}%;background:linear-gradient(90deg, #6a5cff, #7cf0ff)"></div></div>
              <div style="margin-top:.5rem;display:flex;gap:.4rem;flex-wrap:wrap">
                ${ms.status==='disponible'? `<button data-start="${ms.id}" type="button" class="btn btn--primary" style="width:auto;padding:.4rem .6rem;font-size:.74rem">▶ Iniciar</button>`:''}
                ${ms.status==='completada'? `<button data-claim="${ms.id}" type="button" class="btn btn--primary" style="width:auto;padding:.4rem .6rem;font-size:.74rem">🎁 Reclamar ${def.rewards.aureos? `+${def.rewards.aureos} Aurel`:''}</button>`:''}
                <span style="font-size:.72rem;color:var(--muted);align-self:center">${def.rewards.aureos? `Recompensa: ${def.rewards.aureos} Aurel`:''} ${def.rewards.items? `· ${def.rewards.items.map(i=> `${i.qty}× ${getItem(i.id)?.name ?? i.id}`).join(', ')}`:''}</span>
              </div>
            </div>`;
          }).join('')}
        </div>
      `;
      content.querySelectorAll<HTMLButtonElement>('[data-start]').forEach(btn=>{
        btn.addEventListener('click',()=>{
          const id=btn.dataset.start!;
          const st=store.get();
          if(MissionSystem.startMission(st, id)){
            notify('Misión iniciada: '+getMissionDef(id)?.name, 'mission');
            render();
          }
        });
      });
      content.querySelectorAll<HTMLButtonElement>('[data-claim]').forEach(btn=>{
        btn.addEventListener('click',()=>{
          const id=btn.dataset.claim!;
          const st=store.get();
          if(MissionSystem.claimReward(st, id)){
            notify('Recompensa reclamada', 'success');
            render();
          }
        });
      });
    } else if(currentTab==='registro'){
      const s2 = store.get();
      content.innerHTML = `<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:.5rem">
        ${Object.values(s2.registry).length===0? '<div style="color:var(--muted);grid-column:1/-1;text-align:center">Aún no has visto ninguna Asteria. Explora Ruta Aurelia.</div>' : ''}
      </div><div data-role="registry-grid" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:.5rem"></div><div data-role="detail-reg" style="margin-top:.6rem"></div>`;
      const grid = content.querySelector('[data-role="registry-grid"]') as HTMLElement;
      const detailReg = content.querySelector('[data-role="detail-reg"]') as HTMLElement;
      // reutilizar lógica de TeamScreen pero simplificada
      import('../data/asterias.js').then(mod=>{
        for(const sp of mod.ASTERIAS){
          const r = s2.registry[sp.id];
          const seen = r?.seen, caught = r?.caught;
          const div=document.createElement('div');
          div.style.cssText=`padding:.55rem;border-radius:10px;border:1px solid ${caught?'rgba(61,214,140,.35)': seen?'rgba(124,240,255,.25)':'rgba(255,255,255,.06)'};background:${caught?'rgba(61,214,140,.08)': seen?'rgba(124,240,255,.06)':'rgba(255,255,255,.02)'};cursor:pointer`;
          div.innerHTML=`<div style="width:100%;height:64px;border-radius:10px;background:${seen? sp.color:'#1a1a2a'};display:grid;place-items:center;${seen?'':'filter:grayscale(1) opacity(.5)'}"> ${seen?'✦':'???'} </div><div style="font-weight:800;margin-top:.3rem">${seen? sp.name:'???'}</div><div style="font-family:var(--font-mono);font-size:.66rem;color:var(--muted)">${seen? `${sp.element} · ${sp.rarity}`:'No vista'}</div>`;
          div.addEventListener('click',()=>{
            if(!seen){ detailReg.innerHTML='<div class="notice">No vista</div>'; return; }
            detailReg.innerHTML=`<div style="border:1px solid rgba(255,255,255,.1);border-radius:12px;padding:.6rem;background:rgba(255,255,255,.03)"><div style="font-weight:800">${sp.name}</div><div style="font-size:.82rem;color:var(--muted)">${sp.description}</div><div style="font-size:.78rem;color:var(--muted);margin-top:.4rem">${sp.lore}</div></div>`;
          });
          grid.appendChild(div);
        }
      });
    }
  };

  function labelFor(tab:string){
    const map:any = { perfil:'Perfil', equipo:'Equipo', inventario:'Inventario', misiones:'Misiones', registro:'Registro' };
    return map[tab] ?? tab;
  }

  render();
  return el;
}

function notify(msg:string, type:any){
  // @ts-ignore
  import('../systems/notificationSystem.js').then(m=> m.notify(msg, type));
}
